import { Injectable, Inject } from "@angular/core";
import { Observable, of } from "rxjs";
import { HttpClient, HttpErrorResponse, HttpHeaders } from "@angular/common/http";
import { tap, map, debounceTime, distinctUntilChanged, take } from "rxjs/operators";
import { Router } from "@angular/router";
import { DOCUMENT } from "@angular/common";
import { AppError, AppErrorData, UserRightsService } from "../user-rights/user-rights.service";
import { ICustomerGenericService, IGenericService } from "@huge-monorepo/shared/interfaces";
import { ToastrService } from 'ngx-toastr';

declare global {
	interface Navigator {
	  msSaveOrOpenBlob: (blobOrBase64: Blob | string, filename: string) => boolean,
	  msSaveBlob: (blobOrBase64: Blob | string, filename: string) => boolean
	}
}

@Injectable({
	providedIn: "root",
})
export class GenericService implements ICustomerGenericService {
	constructor(
		private http: HttpClient,
		private router: Router,
		private auth: UserRightsService,
		@Inject(DOCUMENT) private document: Document,
		@Inject("WINDOW") private window: any,
    private toastr: ToastrService,
	) {}

	private createHeaders(
		authRequired = true,
		otp = "",
		custom: Object[] = [],
		contentType = "application/json",
	): HttpHeaders {
		let ob = new HttpHeaders();
		if (contentType != "") {
			ob = ob.append("Content-Type", contentType);
		}
		ob = ob.append("X-Huge-Otp", otp);

		if (custom != null) {
			custom.forEach((val) => {
				if (val["Text"] != null && val["Value"] != null) {
					ob = ob.append(val["Text"], val["Value"]);
				}
			});
		}

		if (authRequired === true) {
			ob = ob.append("Authorization", "bearer " + this.auth.GetToken());
		}

		return ob;
	}

	private HandleError(error: HttpErrorResponse, router: Router, requestData: AppErrorData): boolean {
		requestData.Router = router.url;
		this.auth.SetError(
			new AppError(
				error.status,
				error.message,
				error.error.message,
				requestData,
				error.status == 403 || error.status == 500 || error.status == 401,
			),
		);

		switch (error.status) {
			case 403:
				// Forbidden / Unauthorised
				this.auth.Logout();
				break;
			default:
				break;
		}

		return false;
	}

	public DELETE(urlPart: string, requiresAuth = true): Observable<Object> {
		const url: string = urlPart;
		const headers: HttpHeaders = this.createHeaders(requiresAuth);
		const stack = new Error().stack;

		return this.http
			.delete(url, {
				headers: headers,
				responseType: "json",
			})
			.pipe(
				debounceTime(100),
				distinctUntilChanged(),
				tap(
					() => {},
					(error: HttpErrorResponse) => {
						const requestData = new AppErrorData(
							url,
							stack,
							{
								AuthRequired: requiresAuth,
								Headers: headers.getAll("headers"),
							},
							this.router.url,
						);
						this.HandleError(error, this.router, requestData);
					},
				),
			)
			.pipe(take(1));
	}

	public PUT(urlPart: string, objToSend: Object, requiresAuth = true): Observable<Object> {
		const url: string = urlPart;
		const stack = new Error().stack;
		const headers = this.createHeaders(requiresAuth);
		return this.http
			.put(url, objToSend, {
				headers: headers,
				responseType: "json",
			})
			.pipe(
				debounceTime(100),
				distinctUntilChanged(),
				tap(
					() => {},
					(error: HttpErrorResponse) => {
						const requestData = new AppErrorData(
							url,
							stack,
							{
								AuthRequired: requiresAuth,
								Headers: headers.getAll("headers"),
							},
							this.router.url,
						);
						this.HandleError(error, this.router, requestData);
					},
				),
			)
			.pipe(take(1));
	}

	public GET(urlPart: string, requiresAuth = true, response: any = "json", otp = ""): Observable<Object> {
		const url: string = urlPart;
		const stack = new Error().stack;
		const headers = this.createHeaders(requiresAuth, otp);
		return this.http
			.get(url, {
				headers: headers,
				responseType: response,
				observe: "response",
			})
			.pipe(
				debounceTime(100),
				distinctUntilChanged(),
				tap(
					() => {},
					(error: HttpErrorResponse) => {
						const requestData = new AppErrorData(
							url,
							stack,
							{
								AuthRequired: requiresAuth,
								Headers: headers.getAll("headers"),
							},
							this.router.url,
						);
						this.HandleError(error, this.router, requestData);
					},
				),
			)
			.pipe(take(1));
	}

	public POST(
		urlPart: string,
		objectToSend: Object,
		requiresAuth = true,
		customHeaders: Object[] = [],
		contentType = "application/json",
	): Observable<Object> {
		const url: string = urlPart;
		const headers: HttpHeaders = this.createHeaders(requiresAuth, "", customHeaders, contentType);

		const stack = new Error().stack;

		return this.http
			.post(url, objectToSend, {
				headers: headers,
				responseType: "json",
				observe: "response",
			})
			.pipe(
				debounceTime(100),
				distinctUntilChanged(),
				tap(
					() => {},
					(error: HttpErrorResponse) => {
						const requestData = new AppErrorData(
							url,
							stack,
							{
								AuthRequired: requiresAuth,
								Headers: headers.getAll("headers"),
							},
							this.router.url,
						);
						this.HandleError(error, this.router, requestData);
					},
				),
			)
			.pipe(take(1));
	}

	public POSTANDOBSERVE(
		urlPart: string,
		objectToSend: Object,
		requiresAuth: boolean = true,
		customHeaders: Object[] = [],
		contentType: string = "application/json",
	  ): Observable<Object> {
		const url: string = urlPart;
		const headers: HttpHeaders = this.createHeaders(requiresAuth, "", customHeaders, contentType);

		const stack = new Error().stack; // Capture stack trace for debugging purposes

		return this.http
		  .post(url, objectToSend, {
			headers: headers,
			responseType: "json",
			observe: "response",
		  })
		  .pipe(
			debounceTime(100), // Debounce rapid requests
			distinctUntilChanged(), // Ignore duplicate consecutive responses
			tap(
			  () => {}, // Placeholder for any additional processing of successful responses
			  (error: HttpErrorResponse) => {
				const requestData = new AppErrorData(
				  url,
				  stack,
				  {
					AuthRequired: requiresAuth,
					Headers: headers.getAll("headers"),
				  },
				  this.router.url,
				);
				this.HandleError(error, this.router, requestData); // Reuse the same error handling mechanism
			  },
			),
			take(1), // Automatically unsubscribe after one emission
		  );
	  }

	downloadFile(urlPart: string, objectToSend: any, method = "post", stack: string): Observable<any> {
		const headers = new HttpHeaders({
			"Content-Type": "application/json",
			"Authorization": "bearer " + this.auth.GetToken(),
		});

		switch (method) {
			case "get":
				return this.http
					.get(urlPart, {
						headers: headers,
						observe: "response",
						responseType: "blob",
					})
					.pipe(
						debounceTime(100),
						distinctUntilChanged(),
						tap(
							// Log the result or error
							() => {},
							(error: HttpErrorResponse) => {
								const requestData = new AppErrorData(
									urlPart,
									stack,
									{
										AuthRequired: true,
										Headers: headers.getAll("headers"),
									},
									this.router.url,
								);
								this.HandleError(error, this.router, requestData);
							},
						),
					)
					.pipe(take(1));
			default:
				return this.http
					.post(urlPart, objectToSend, {
						headers: headers,
						observe: "response",
						responseType: "blob",
					})
					.pipe(
						debounceTime(100),
						distinctUntilChanged(),
						tap(
							// Log the result or error
							() => {},
							(error: HttpErrorResponse) => {
								const requestData = new AppErrorData(
									urlPart,
									stack,
									{
										AuthRequired: true,
										Headers: headers.getAll("headers"),
									},
									this.router.url,
								);
								this.HandleError(error, this.router, requestData);
							},
						),
					)
					.pipe(take(1));
		}
	}

	public download(urlPart: string, obj: Object, method = "post", toDownload = true): Observable<any> {
		const stack = new Error().stack;
		const url: string = urlPart;
		return this.downloadFile(url, obj, method, stack).pipe(
			map((data) => {
				if (toDownload == true) {
					const blob = new Blob([data.body], {
						type: data.headers.get("content-type"),
					});
					const disposition = data.headers.get("Content-Disposition");
					const fileName = disposition.substring(21, disposition.length);
					const linkURL = this.window.URL.createObjectURL(blob);
					if (window.navigator.msSaveOrOpenBlob) {
						//IE & Edge
						//msSaveBlob only available for IE & Edge
						window.navigator.msSaveBlob(data, fileName);
					} //Chrome & FF
					else {
						const anchor = this.document.createElement("a");
						anchor.href = linkURL;
						anchor.download = fileName;
						this.document.body.appendChild(anchor); //For FF
						anchor.click();
						//It's better to remove the elem
						this.document.body.removeChild(anchor);
					}
				} else {
					const blob = new Blob([data.body], { type: "application/pdf" });
					const disposition = data.headers.get("Content-Disposition");
					const fileName = disposition.substring(21, disposition.length);
					const linkURL = this.window.URL.createObjectURL(blob);
					window.open(linkURL, fileName);
				}

				return of(true);
			}),
		);
	}

  public createToast(header: string, description: string, type: string = 'success', timeout: number = 3) {
    this.toastr[type](description, header, {
      extendedTimeOut: timeout * 1000,
      progressBar: true,
    });
  }
}
